import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  MarkerType,
  type NodeTypes,
  type NodeProps,
  type Node,
  type EdgeProps,
  type EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Link } from '@tanstack/react-router';
import { DiamondIcon, XIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type {
  MockUiEvalCase,
  MockUiStateGraph,
  MockUiStoryboard,
} from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { useAutoLayout } from 'src/hooks/useAutoLayout';
import {
  buildPathTree,
  graphToFlow,
  type PathNode,
  type PathNodeData,
} from '../pathTree';

/** One node — a *state* of the built mock: its name, a `?case&step` screenshot, and (for a leaf) a
 * terminal marker. Click it for the detail overlay. */
function PathTreeNode({ data, selected }: NodeProps<PathNode>) {
  return (
    <div
      className={`w-44 rounded-md border bg-background p-1.5 shadow-sm ${
        selected ? 'ring-2 ring-primary' : ''
      } ${data.isTerminal ? 'border-primary/60' : ''}`}
    >
      {!data.isRoot && <Handle type="target" position={Position.Left} />}
      {data.isRoot ? (
        <div className="flex h-24 items-center justify-center text-xs font-semibold text-muted-foreground">
          <T context="Root node of the mock UI state machine (the initial screen)">
            Start
          </T>
        </div>
      ) : (
        <>
          <div className="mb-1 flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
              {data.label}
            </span>
            {data.isTerminal && (
              <DiamondIcon
                className="h-3 w-3 shrink-0 text-primary"
                weight="fill"
              />
            )}
          </div>
          {data.screenshot ? (
            <img
              src={data.screenshot}
              alt={data.label}
              className="h-24 w-full rounded object-cover object-top"
            />
          ) : (
            <div className="flex h-24 items-center justify-center rounded border border-dashed text-[10px] text-muted-foreground">
              <T context="placeholder in a state node with no captured screenshot">
                no render
              </T>
            </div>
          )}
          {data.screen && (
            <div className="mt-1 truncate text-[10px] text-muted-foreground">
              {data.screen}
            </div>
          )}
          {data.caseIds.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {data.caseIds.map((id) => (
                <Badge
                  key={id}
                  variant="secondary"
                  className="max-w-full text-[9px]"
                >
                  {id}
                </Badge>
              ))}
            </div>
          )}
        </>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

/** A read-only action edge: an arrow with the full action label in a wrapping chip (no truncation).
 * Mirrors the storyboard editor's edge, minus the inline editing. */
function StateActionEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  markerEnd,
}: EdgeProps) {
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const text = typeof label === 'string' ? label : '';
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{ strokeWidth: 1.5, stroke: '#94a3b8' }}
      />
      {text && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'none',
            }}
            className="nodrag nopan max-w-[140px] whitespace-normal rounded border bg-background px-1 py-0.5 text-center text-[10px] leading-tight text-muted-foreground"
          >
            {text}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes: NodeTypes = { pathNode: PathTreeNode };
const edgeTypes: EdgeTypes = { stateEdge: StateActionEdge };
const defaultEdgeOptions = {
  type: 'stateEdge',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 16,
    height: 16,
    color: '#94a3b8',
  },
};

/**
 * An auto-laid-out **state machine** of the mock: nodes are states (their `?case&step` screenshot +
 * captured `window.__evalState`), edges are the actions between them. Prefers the persisted
 * `stateGraph` (a real DAG that reconverges on shared states); falls back to deriving a trie from
 * the flat `cases` for legacy/in-flight builds. Read-only — click a state for the detail overlay.
 * Reuses the workflows editor's ELK layout (`useAutoLayout`).
 */
export function MockUiPathTree({
  stateGraph,
  cases,
  storyboard,
}: {
  stateGraph?: MockUiStateGraph | null;
  cases: MockUiEvalCase[];
  storyboard?: MockUiStoryboard | null;
}) {
  const graph = useMemo(
    () =>
      stateGraph && stateGraph.nodes.length > 0
        ? graphToFlow(stateGraph)
        : buildPathTree(cases),
    [stateGraph, cases],
  );
  const { getAutoLayoutedNodes } = useAutoLayout();
  const [nodes, setNodes] = useState<Node[]>(graph.nodes);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getAutoLayoutedNodes(graph.nodes, graph.edges).then((laid) => {
      if (!cancelled) setNodes(laid);
    });
    return () => {
      cancelled = true;
    };
  }, [graph, getAutoLayoutedNodes]);

  // We own selection (so the ✕ can clear the ring + overlay together) — inject `selected` onto the
  // nodes rather than letting React Flow manage it.
  const displayNodes = useMemo(
    () => nodes.map((n) => ({ ...n, selected: n.id === selectedId })),
    [nodes, selectedId],
  );
  const selectedData = useMemo(
    () =>
      (nodes.find((n) => n.id === selectedId)?.data as
        PathNodeData | undefined) ?? null,
    [nodes, selectedId],
  );

  const onNodeClick = useCallback((_event: MouseEvent, node: Node) => {
    setSelectedId(node.id);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <StateMachineLegend />
      <div className="relative min-h-0 flex-1">
        <ReactFlow
          nodes={displayNodes}
          edges={graph.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
          minZoom={0.2}
          maxZoom={2}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          onNodeClick={onNodeClick}
          onPaneClick={() => setSelectedId(null)}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
        {selectedData && (
          <StateDetailCard
            data={selectedData}
            storyboard={storyboard ?? null}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </div>
  );
}

/** What the glyphs mean, so the graph reads as a state machine at a glance. */
function StateMachineLegend() {
  return (
    <div className="flex items-center gap-3 px-1 pb-1 text-[10px] text-muted-foreground">
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full border" />
        <T context="state-machine legend: a node is a state">state</T>
      </span>
      <span className="flex items-center gap-1">
        →<T context="state-machine legend: an edge is an action">action</T>
      </span>
      <span className="flex items-center gap-1">
        <DiamondIcon className="h-2.5 w-2.5 text-primary" weight="fill" />
        <T context="state-machine legend: a leaf state ends a path">terminal</T>
      </span>
    </div>
  );
}

/** Compact key/value list for a `window.__evalState` / expected-state object. */
function KeyVals({
  title,
  obj,
}: {
  title: ReactNode;
  obj: Record<string, unknown>;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase text-muted-foreground">
        {title}
      </div>
      <dl className="mt-0.5 space-y-0.5">
        {Object.entries(obj).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt className="truncate text-muted-foreground">{k}</dt>
            <dd className="truncate font-mono">
              {typeof v === 'string' ? v : JSON.stringify(v)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Floating detail for the selected state — overlays the canvas (doesn't shrink it): the built
 * screenshot next to its source storyboard frame, the captured + expected `window.__evalState`, and
 * which cases pass through here. */
function StateDetailCard({
  data,
  storyboard,
  onClose,
}: {
  data: PathNodeData;
  storyboard: MockUiStoryboard | null;
  onClose: () => void;
}) {
  const gt = useGT();
  const snapshot = data.evalStateSnapshot;
  const hasSnapshot = snapshot && Object.keys(snapshot).length > 0;
  const hasExpect = data.expect && Object.keys(data.expect).length > 0;
  const step = data.anchor?.step ?? -1;
  const source = step >= 0 ? storyboard?.[step]?.source : undefined;
  return (
    <div className="absolute right-2 top-2 z-10 flex max-h-[calc(100%-1rem)] w-72 flex-col gap-2 overflow-y-auto rounded-md border bg-background p-2 text-xs shadow-lg">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-medium">
          {data.screen || data.label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 shrink-0 p-0"
          onClick={onClose}
          aria-label={gt('Close state details')}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>

      {source ? (
        <div className="grid grid-cols-2 gap-1">
          <figure className="space-y-0.5">
            <figcaption className="text-[10px] text-muted-foreground">
              <T context="label for the source storyboard frame of a state">
                Source
              </T>
            </figcaption>
            <img src={source} alt="" className="w-full rounded border" />
          </figure>
          <figure className="space-y-0.5">
            <figcaption className="text-[10px] text-muted-foreground">
              <T context="label for the built screen of a state">Build</T>
            </figcaption>
            {data.screenshot ? (
              <img
                src={data.screenshot}
                alt=""
                className="w-full rounded border"
              />
            ) : (
              <div className="flex h-16 items-center justify-center rounded border border-dashed text-[10px] text-muted-foreground">
                <T context="placeholder when a state has no captured render">
                  no render
                </T>
              </div>
            )}
          </figure>
        </div>
      ) : (
        data.screenshot && (
          <img
            src={data.screenshot}
            alt={data.label}
            className="w-full rounded border object-contain"
          />
        )
      )}

      {data.task && <div>{data.task}</div>}

      {hasSnapshot && snapshot && (
        <KeyVals
          title={
            <T context="Label for the captured window.__evalState of a state node">
              Captured state
            </T>
          }
          obj={snapshot}
        />
      )}
      {hasExpect && data.expect && (
        <KeyVals
          title={
            <T context="Label for the expected end-state of a path in the state graph">
              Expected
            </T>
          }
          obj={data.expect}
        />
      )}
      {data.golden && Object.keys(data.golden).length > 0 && (
        <KeyVals
          title={
            <T context="Label for the golden window.__evalState the deterministic eval grades against">
              Golden (eval assertion)
            </T>
          }
          obj={data.golden}
        />
      )}
      {data.sopId && (
        <Link
          to="/agents/procedures/$id"
          params={{ id: data.sopId }}
          className="text-primary underline underline-offset-2"
        >
          <T context="link from a state node to the SOP (procedure) taught for its path">
            Open procedure (SOP) ↗
          </T>
        </Link>
      )}
      {data.caseIds.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold uppercase text-muted-foreground">
            <T context="label listing which eval cases pass through this state">
              Cases here
            </T>
          </span>{' '}
          <span className="font-mono">{data.caseIds.join(', ')}</span>
        </div>
      )}
    </div>
  );
}
